
import React, { useState, useEffect, useMemo } from 'react';
import type { QuizSettings, DrugInfo } from '../types';
import { getDrugById } from '../services/pharmacyService';
import { addMissedCard, removeMissedCard, toggleBookmark, isBookmarked } from '../services/studyService';
import { useAuth } from '../contexts/AuthContext';

// --- ICONS ---
const CloseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>;
const PrevIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>;
const NextIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>;
const StarIcon: React.FC<{ filled: boolean; className?: string }> = ({ filled, className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={`h-8 w-8 transition-colors ${className} ${filled ? 'text-yellow-400 fill-current' : 'text-gray-600 hover:text-yellow-400'}`} viewBox="0 0 20 20" fill="currentColor">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
);


interface QuizProps {
    settings: QuizSettings;
    onEndQuiz: () => void;
}

const Quiz: React.FC<QuizProps> = ({ settings, onEndQuiz }) => {
    const { uid } = useAuth();
    const [cards, setCards] = useState<DrugInfo[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isFlipped, setIsFlipped] = useState(false);
    const [isCurrentCardBookmarked, setIsCurrentCardBookmarked] = useState(false);
    
    const currentCard = useMemo(() => cards[currentIndex], [cards, currentIndex]);
    
    useEffect(() => {
        if (!uid) return;
        const fetchCards = async () => {
            setIsLoading(true);
            const drugDataPromises = settings.drugIds.map(id => getDrugById(id, uid));
            const results = await Promise.all(drugDataPromises);
            setCards(results.filter(Boolean) as DrugInfo[]);
            setIsLoading(false);
        };
        fetchCards();
    }, [settings.drugIds, uid]);

    useEffect(() => {
        if (currentCard && uid) {
            setIsCurrentCardBookmarked(isBookmarked(currentCard.id, uid));
        }
    }, [currentCard, uid]);

    const goToNextCard = () => {
        if (currentIndex < cards.length - 1) {
            setIsFlipped(false);
            setCurrentIndex(currentIndex + 1);
        } else {
            // End of quiz
            alert("Quiz complete!");
            onEndQuiz();
        }
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setIsFlipped(false);
            setCurrentIndex(currentIndex - 1);
        }
    };

    const handleMarkCorrect = () => {
        if (!currentCard || !uid) return;
        removeMissedCard(currentCard.id, uid);
        goToNextCard();
    };

    const handleMarkIncorrect = () => {
        if (!currentCard || !uid) return;
        addMissedCard(currentCard.id, uid);
        goToNextCard();
    };
    
    const handleToggleBookmark = () => {
        if (!currentCard || !uid) return;
        toggleBookmark(currentCard.id, uid);
        setIsCurrentCardBookmarked(prev => !prev);
    };

    if (isLoading) {
        return <div className="flex items-center justify-center h-screen text-white">Loading quiz...</div>;
    }
    
    if (cards.length === 0 || !currentCard) {
         return (
            <div className="flex flex-col items-center justify-center h-screen text-white">
                <p>No cards available for this quiz.</p>
                <button onClick={onEndQuiz} className="mt-4 bg-yellow-400 text-gray-900 font-bold py-2 px-6 rounded-lg">Back to Setup</button>
            </div>
        );
    }
    
    const progressPercentage = ((currentIndex + 1) / cards.length) * 100;

    return (
        <div className="fixed inset-0 bg-[#101D35] flex flex-col p-4 sm:p-8">
            {/* Header */}
            <header className="flex items-center justify-between mb-4">
                <div className="flex-1">
                    <p className="text-sm text-gray-400">Card {currentIndex + 1} of {cards.length}</p>
                    <div className="w-full bg-blue-900/50 rounded-full h-2 mt-1">
                        <div className="bg-yellow-400 h-2 rounded-full" style={{ width: `${progressPercentage}%` }}></div>
                    </div>
                </div>
                <button onClick={onEndQuiz} className="ml-8 text-gray-400 hover:text-white transition-colors">
                    <CloseIcon />
                </button>
            </header>

            {/* Card */}
            <main className="flex-1 flex flex-col items-center justify-center quiz-card-container">
                <div className="w-full max-w-2xl h-96 relative">
                    <div className={`quiz-card-flipper w-full h-full ${isFlipped ? 'flipped' : ''}`} onClick={() => setIsFlipped(!isFlipped)}>
                        {/* Front of Card */}
                        <div className="quiz-card-front bg-gradient-to-br from-[#1E2E4A] to-[#243B5F] border border-blue-800/50 flex flex-col items-center justify-center p-8 cursor-pointer">
                            <p className="text-gray-400 text-lg">Brand Name</p>
                            <h2 className="text-5xl font-bold text-white text-center">{currentCard.brandName}</h2>
                        </div>
                        {/* Back of Card */}
                        <div className="quiz-card-back bg-gradient-to-br from-[#1E2E4A] to-[#243B5F] border border-blue-800/50 flex flex-col items-center justify-center p-8 cursor-pointer overflow-y-auto">
                           <h3 className="text-3xl font-bold text-yellow-400 text-center">{currentCard.genericName}</h3>
                           <p className="text-gray-400 mt-4 text-center">
                             {typeof currentCard.pharmacology === 'string' 
                               ? currentCard.pharmacology 
                               : currentCard.pharmacology?.mechanismOfAction || "No pharmacology information available."}
                           </p>
                        </div>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="h-40 flex flex-col items-center justify-end">
                {isFlipped ? (
                    <div className="w-full max-w-2xl flex items-center justify-between animate-fade-in">
                        <button onClick={handleMarkIncorrect} className="w-1/2 py-4 text-lg font-bold text-red-300 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-colors mr-2">
                            Incorrect
                        </button>
                        <button onClick={handleToggleBookmark}>
                           <StarIcon filled={isCurrentCardBookmarked} />
                        </button>
                        <button onClick={handleMarkCorrect} className="w-1/2 py-4 text-lg font-bold text-green-300 bg-green-500/10 hover:bg-green-500/20 rounded-lg transition-colors ml-2">
                            Correct
                        </button>
                    </div>
                ) : (
                    <div className="text-gray-400 font-semibold">Click card to flip</div>
                )}
                 <div className="w-full max-w-3xl flex justify-between items-center mt-4">
                    <button onClick={handlePrev} disabled={currentIndex === 0} className="p-4 rounded-full bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed">
                        <PrevIcon />
                    </button>
                    <button onClick={goToNextCard} className="p-4 rounded-full bg-white/5 hover:bg-white/10">
                        <NextIcon />
                    </button>
                </div>
            </footer>
        </div>
    );
};

export default Quiz;
